/**
 * All functions for Keycloak login
 */
import Keycloak from "keycloak-js";
import { updateBackendConfiguration } from "../../../app/backend";

// Runtime configuration state
let runtimeConfig: any = null;
let keycloak: Keycloak | null = null;
let configPromise: Promise<void> | null = null;

const isValidHttpUrl = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("http");

const validateRuntimeConfig = (config: any): void => {
  const hasDualConfig = Boolean(config.oidcIssuerOld && config.oidcIssuerNew);
  const hasSingleConfig = Boolean(config.oidcIssuer);

  if (!hasDualConfig && !hasSingleConfig) {
    throw new Error("Invalid runtime configuration: missing oidcIssuer or dual configuration");
  }

  if (!config.apiUrl || !config.orgApiUrl) {
    throw new Error("Invalid runtime configuration: missing apiUrl or orgApiUrl");
  }

  if (!isValidHttpUrl(config.apiUrl)) {
    throw new Error("Invalid runtime configuration: apiUrl is not a valid URL");
  }

  if (!isValidHttpUrl(config.orgApiUrl)) {
    throw new Error("Invalid runtime configuration: orgApiUrl is not a valid URL");
  }
};

const logRuntimeConfiguration = (config: any): void => {
  const hasDualConfig = Boolean(config.oidcIssuerOld && config.oidcIssuerNew);

  // eslint-disable-next-line no-console
  console.log("🔧 Using ON-PREMISE runtime configuration");
  if (hasDualConfig) {
    // eslint-disable-next-line no-console
    console.log("📍 Keycloak URLs:", {
      old: config.oidcIssuerOld,
      new: config.oidcIssuerNew,
      migratedCustomers: config.migratedCustomers || []
    });
  } else {
    // eslint-disable-next-line no-console
    console.log("📍 Keycloak URL:", config.oidcIssuer);
  }
  // eslint-disable-next-line no-console
  console.log("🌐 API URL:", config.apiUrl);
  // eslint-disable-next-line no-console
  console.log("🏢 Org API URL:", config.orgApiUrl);
};

const applyRuntimeConfiguration = (config: any): void => {
  runtimeConfig = config;

  (globalThis as any).REACT_APP_GOOGLE_MAPS_API_KEY = config.googleMapsKey;
  (globalThis as any).REACT_APP_BOOKING_BACKEND_URL = config.apiUrl;
  (globalThis as any).REACT_APP_ORG_BACKEND_URL = config.orgApiUrl;
  (globalThis as any).REACT_APP_FRONTEND_URL = config.baseUrl;

  updateBackendConfiguration();
};

const getEnvironmentLabel = (keycloakUrl: string): string => {
  if (keycloakUrl.includes("qa.seedit.cloud")) {
    return "QA environment";
  }

  if (keycloakUrl.includes("staging.seedit.cloud")) {
    return "STAGING environment";
  }

  if (keycloakUrl.includes("demo.seedit.cloud")) {
    return "DEMO environment";
  }

  if (
    keycloakUrl.includes("seedit.cloud") &&
    !keycloakUrl.includes("qa") &&
    !keycloakUrl.includes("staging")
  ) {
    return "PRODUCTION environment";
  }

  if (keycloakUrl.includes("localhost") || keycloakUrl.includes("127.0.0.1")) {
    return "LOCAL development";
  }

  return "UNKNOWN environment";
};

const clearRuntimeConfiguration = (): void => {
  runtimeConfig = null;
  delete (globalThis as any).REACT_APP_BOOKING_BACKEND_URL;
  delete (globalThis as any).REACT_APP_ORG_BACKEND_URL;
  delete (globalThis as any).REACT_APP_FRONTEND_URL;
  delete (globalThis as any).REACT_APP_GOOGLE_MAPS_API_KEY;
};

const logRuntimeConfigFallback = (errorMessage: string): void => {
  const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL ?? "";
  const environmentLabel = getEnvironmentLabel(keycloakUrl);

  // eslint-disable-next-line no-console
  console.log(`🔧 Using ${environmentLabel} configuration`);
  // eslint-disable-next-line no-console
  console.log("📍 Keycloak URL:", keycloakUrl);
  // eslint-disable-next-line no-console
  console.log("⚠️ Runtime configuration not available:", errorMessage);
};

const fetchRuntimeConfiguration = async (): Promise<any> => {
  const res = await fetch(globalThis.location.origin + "/config.json");
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  }

  if (!res.headers.get("content-type")?.includes("application/json")) {
    throw new Error("Response is not JSON - likely running in development mode");
  }

  return res.json();
};

// Helper function to extract realm from issuer URL
const extractRealmFromIssuer = (issuer: string): string | null => {
  // Issuer format: https://domain/auth/realms/REALM_NAME
  const regex = /\/realms\/([^/]+)/;
  const match = regex.exec(issuer);
  return match ? match[1] : null;
};

// Helper function to update localStorage preference based on token issuer
const updateKeycloakUrlPreference = (token: string, config: any): void => {
  if (!config.oidcIssuerOld || !config.oidcIssuerNew) {
    return;
  }

  try {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) {
      return;
    }

    const payload = JSON.parse(atob(tokenParts[1]));
    if (!payload.iss) {
      return;
    }

    const tokenRealm = extractRealmFromIssuer(payload.iss);
    if (!tokenRealm) {
      return;
    }

    // Check if issuer matches new URL pattern (base domain, not identity subdomain)
    // Extract base URLs from config (remove /auth/realms/master suffix)
    // Note: We already validated both values exist at line 22, so they are guaranteed to be strings
    const newUrlBase = config.oidcIssuerNew.replace("/auth/realms/master", "");
    const oldUrlBase = config.oidcIssuerOld.replace("/auth/realms/master", "");

    const isNewUrl =
      newUrlBase && payload.iss.includes(newUrlBase) && !payload.iss.includes("identity");
    const isOldUrl =
      oldUrlBase && payload.iss.includes(oldUrlBase) && payload.iss.includes("identity");

    if (isNewUrl) {
      localStorage.setItem(`keycloak_url_${tokenRealm}`, "new");
    } else if (isOldUrl) {
      localStorage.setItem(`keycloak_url_${tokenRealm}`, "old");
    }
  } catch {
    // Ignore errors when parsing token
  }
};

// Helper function to get Keycloak URL based on configuration
const getKeycloakUrl = (config: any): string => {
  // Check if dual configuration is enabled
  if (config.oidcIssuerOld && config.oidcIssuerNew) {
    // Always use new URL (base domain) for Keycloak during dual-mode
    // This ensures redirect URIs match regardless of frontend access URL
    // The redirect URI is constructed by Keycloak based on the Keycloak URL we provide,
    // so using the base domain ensures it always matches Microsoft Entra ID configuration
    return config.oidcIssuerNew;
  }

  // Single configuration (backward compatible)
  return config.oidcIssuer || (process.env.REACT_APP_KEYCLOAK_URL ?? "");
};

// Function to get or create Keycloak instance
const getKeycloakInstance = async (): Promise<Keycloak> => {
  if (keycloak) {
    return keycloak;
  }

  // Wait for runtime configuration to be loaded
  if (configPromise) {
    await configPromise;
  }

  // Create Keycloak instance with runtime config if available, otherwise use build-time config
  const config = runtimeConfig ?? {
    oidcIssuer: process.env.REACT_APP_KEYCLOAK_URL ?? "",
    oidcClientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? "",
    oidcRedirectUri: globalThis.location.origin + "/",
    oidcPostLogoutRedirectUri: globalThis.location.origin + "/"
  };

  // Determine which URL to use
  const keycloakUrl = getKeycloakUrl(config);

  keycloak = new Keycloak({
    clientId: config.oidcClientId || (process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? ""),
    realm: config.oidcRealm ?? process.env.REACT_APP_KEYCLOAK_REALM ?? "",
    url: keycloakUrl
  });

  // After authentication success, check token issuer and update preference
  keycloak.onAuthRefreshSuccess = () => {
    if (keycloak?.token) {
      updateKeycloakUrlPreference(keycloak.token, config);
    }
  };

  return keycloak;
};

// Load runtime configuration
const loadRuntimeConfiguration = async (): Promise<void> => {
  try {
    const config = await fetchRuntimeConfiguration();
    validateRuntimeConfig(config);
    logRuntimeConfiguration(config);
    applyRuntimeConfiguration(config);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    clearRuntimeConfiguration();
    logRuntimeConfigFallback(errorMessage);
  }
};

configPromise = loadRuntimeConfiguration();

export default getKeycloakInstance;
